import React, { useState, useEffect, useMemo } from 'react';
import { http } from '../lib/http';
import { Icon } from '@iconify/react';
import jsPDF from 'jspdf';
import './ClosedCallsView.css';

interface ClosedCall {
  id: string;
  incident_number: string;
  call_code: string;
  location: string;
  notes: string;
  status: string;
  priority: number;
  created_at: string;
  closed_at: string;
  closed_by: string;
  reopened_at?: string;
  reopened_by?: string;
  reopen_count: number;
  dispatch_time?: number;
  response_time?: number;
  enroute_time?: number;
  on_scene_time?: number;
  transport_time?: number;
  total_duration?: number;
  units_assigned_count?: number;
  units_responded_count?: number;
  comment_count?: number;
  first_unit_assigned_at?: string;
  first_unit_enroute_at?: string;
  first_unit_onscene_at?: string;
  first_unit_transport_at?: string;
  all_units_clear_at?: string;
  units?: any; // Unit details for per-unit breakdown
  unit_breakdown?: any; // Detailed unit breakdown from metrics
}

interface ClosedCallsViewProps {
  onClose: () => void;
  userRole: string; // 'dispatcher', 'admin', 'unit'
}

const ClosedCallsView: React.FC<ClosedCallsViewProps> = ({ onClose, userRole }) => {
  const [allCalls, setAllCalls] = useState<ClosedCall[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedCallCode, setSelectedCallCode] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const callsPerPage = 25;
  
  // Selected call for details
  const [selectedCall, setSelectedCall] = useState<ClosedCall | null>(null);
  const [callDetails, setCallDetails] = useState<any>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  // Reopen modal
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [reopenComment, setReopenComment] = useState('');
  const [reopenReason, setReopenReason] = useState('Additional response needed');
  
  // Metrics summary (removed unused variables)

  const isDispatcher = userRole === 'dispatcher' || userRole === 'admin';

  // Client-side filtering
  const filteredCalls = useMemo(() => {
    let filtered = [...allCalls];

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(call => 
        call.location?.toLowerCase().includes(searchLower) ||
        call.notes?.toLowerCase().includes(searchLower) ||
        call.incident_number?.toLowerCase().includes(searchLower)
      );
    }

    // Date range filter
    if (startDate) {
      const startTimestamp = new Date(startDate).getTime();
      filtered = filtered.filter(call => {
        const closedTimestamp = new Date(call.closed_at).getTime();
        return closedTimestamp >= startTimestamp;
      });
    }

    if (endDate) {
      const endTimestamp = new Date(endDate).getTime() + (24 * 60 * 60 * 1000 - 1); // End of day
      filtered = filtered.filter(call => {
        const closedTimestamp = new Date(call.closed_at).getTime();
        return closedTimestamp <= endTimestamp;
      });
    }

    // Call code filter
    if (selectedCallCode) {
      filtered = filtered.filter(call => call.call_code === selectedCallCode);
    }

    return filtered;
  }, [allCalls, searchTerm, startDate, endDate, selectedCallCode]);

  // Client-side pagination
  const paginatedCalls = useMemo(() => {
    const startIndex = (currentPage - 1) * callsPerPage;
    const endIndex = startIndex + callsPerPage;
    return filteredCalls.slice(startIndex, endIndex);
  }, [filteredCalls, currentPage, callsPerPage]);

  const totalPages = Math.ceil(filteredCalls.length / callsPerPage);
  const hasMore = currentPage < totalPages;

  // Calculate metrics from filtered data
  const calculatedMetrics = useMemo(() => {
    if (filteredCalls.length === 0) return null;

    const totalReopens = filteredCalls.reduce((sum, call) => sum + (call.reopen_count || 0), 0);
    
    const callsWithResponseTime = filteredCalls.filter(c => c.response_time && c.response_time > 0);
    const avgResponseTime = callsWithResponseTime.length > 0
      ? callsWithResponseTime.reduce((sum, c) => sum + (c.response_time || 0), 0) / callsWithResponseTime.length
      : 0;

    const callsWithOnSceneTime = filteredCalls.filter(c => c.on_scene_time && c.on_scene_time > 0);
    const avgOnSceneTime = callsWithOnSceneTime.length > 0
      ? callsWithOnSceneTime.reduce((sum, c) => sum + (c.on_scene_time || 0), 0) / callsWithOnSceneTime.length
      : 0;

    const callsWithTotalDuration = filteredCalls.filter(c => c.total_duration && c.total_duration > 0);
    const avgTotalDuration = callsWithTotalDuration.length > 0
      ? callsWithTotalDuration.reduce((sum, c) => sum + (c.total_duration || 0), 0) / callsWithTotalDuration.length
      : 0;

    return {
      total_calls: filteredCalls.length,
      total_reopens: totalReopens,
      avg_response_time: avgResponseTime,
      avg_on_scene_time: avgOnSceneTime,
      avg_total_duration: avgTotalDuration
    };
  }, [filteredCalls]);

  useEffect(() => {
    fetchClosedCalls();
  }, []);

  const fetchClosedCalls = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
      setLoading(true);
      }
      setError(null);

      // Load all closed calls at once (limit set to 1000 - adjust if needed)
      const response = await http.get(`/closed-calls?limit=1000&offset=0`);
      
      setAllCalls(response.data.calls);
      console.log('[CLOSED CALLS] Loaded', response.data.calls.length, 'closed calls for client-side filtering');
    } catch (err: any) {
      console.error('[CLOSED CALLS] Error fetching:', err);
      const errorMsg = typeof err.response?.data?.error === 'string'
        ? err.response.data.error
        : 'Failed to load closed calls';
      setError(errorMsg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchClosedCalls(true);
  };

  const fetchCallDetails = async (call: ClosedCall) => {
    try {
      const response = await http.get(`/closed-calls/${call.id}`);
      setCallDetails(response.data);
      setSelectedCall(call);
      setShowDetailsModal(true);
    } catch (err) {
      console.error('[CLOSED CALLS] Error fetching details:', err);
      alert('Failed to load call details');
    }
  };

  const handleReopenCall = async () => {
    if (!selectedCall) return;

    try {
      await http.post(`/closed-calls/${selectedCall.id}/reopen`, {
        comment: reopenComment,
        reason: reopenReason
      });

      alert(`Call ${selectedCall.incident_number} reopened successfully`);
      setShowReopenModal(false);
      setShowDetailsModal(false);
      setReopenComment('');
      fetchClosedCalls(true);
    } catch (err: any) {
      console.error('[CLOSED CALLS] Error reopening:', err);
      const errorMsg = typeof err.response?.data?.error === 'string'
        ? err.response.data.error
        : 'Failed to reopen call';
      alert(errorMsg);
    }
  };

  // Removed unused handleSetPending function

  const formatDuration = (seconds: number | null | undefined): string => {
    if (!seconds || seconds < 0) return 'N/A';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    const parts: string[] = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

    return parts.join(' ');
  };

  const formatDateTime = (dateString: string): string => {
    return new Date(dateString).toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const exportToPDF = async (call: ClosedCall) => {
    try {
      // Fetch full details if we need status history and comments
      let details = callDetails;
      if (!details || details.call?.id !== call.id) {
        const response = await http.get(`/closed-calls/${call.id}`);
        details = response.data;
      }

      // Create PDF document
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      let yPos = 20;

      // Helper to add text with word wrapping
      const addText = (text: string, fontSize: number = 10, isBold: boolean = false) => {
        doc.setFontSize(fontSize);
        if (isBold) {
          doc.setFont('helvetica', 'bold');
        } else {
          doc.setFont('helvetica', 'normal');
        }
        
        const lines = doc.splitTextToSize(text || 'N/A', contentWidth);
        lines.forEach((line: string) => {
          if (yPos > 270) {
            doc.addPage();
            yPos = 20;
          }
          doc.text(line, margin, yPos);
          yPos += fontSize * 0.5;
        });
        yPos += 2;
      };

      const addSection = (title: string) => {
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }
        yPos += 5;
        doc.setFillColor(52, 152, 219);
        doc.rect(margin, yPos - 5, contentWidth, 8, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(title, margin + 2, yPos);
        yPos += 8;
        doc.setTextColor(0, 0, 0);
      };

      // Title
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Call Report', margin, yPos);
      yPos += 10;

      // Header Info
      addSection('Call Information');
      addText(`Incident Number: ${call.incident_number}`, 10, true);
      addText(`Call Code: ${call.call_code}`);
      addText(`Location: ${call.location}`);
      addText(`Priority: P${call.priority}`);
      addText(`Status: ${call.status}`);
      addText(`Created: ${formatDateTime(call.created_at)}`);
      if (call.closed_at) {
        addText(`Closed: ${formatDateTime(call.closed_at)}`);
        addText(`Closed By: ${call.closed_by || 'Unknown'}`);
      }
      if (call.reopen_count > 0) {
        addText(`Reopen Count: ${call.reopen_count}`, 10, true);
      }

      // Notes
      if (call.notes) {
        yPos += 3;
        addText('Notes:', 10, true);
        addText(call.notes);
      }

      // Performance Metrics with Timestamps
      addSection('Performance Metrics & Timeline');
      
      // Call Created
      addText(`Call Created (Pending): ${formatDateTime(call.created_at)}`, 10, true);
      yPos += 2;
      
      // First Unit Assigned
      if (call.first_unit_assigned_at) {
        addText(`First Unit Assigned (Active): ${formatDateTime(call.first_unit_assigned_at)}`, 10, true);
        addText(`   Dispatch Time: ${formatDuration(call.dispatch_time)} (time to assign unit)`);
        yPos += 2;
      }
      
      // First Unit Enroute
      if (call.first_unit_enroute_at) {
        addText(`First Unit Enroute: ${formatDateTime(call.first_unit_enroute_at)}`, 10, true);
        addText(`   Response Time: ${formatDuration(call.response_time)} (time to acknowledge)`);
        yPos += 2;
      }
      
      // First Unit On Scene
      if (call.first_unit_onscene_at) {
        addText(`First Unit On Scene: ${formatDateTime(call.first_unit_onscene_at)}`, 10, true);
        addText(`   Enroute Time: ${formatDuration(call.enroute_time)}`);
        yPos += 2;
      }
      
      // Call Closed
      addText(`Call Closed: ${formatDateTime(call.closed_at)}`, 10, true);
      if (call.first_unit_onscene_at) {
        addText(`   On-Scene Time: ${formatDuration(call.on_scene_time)}`);
      }
      yPos += 2;
      
      // Summary
      addText(`Total Duration: ${formatDuration(call.total_duration)}`, 11, true);
      addText(`Units Assigned: ${call.units_assigned_count || 0}  |  Units Responded: ${call.units_responded_count || 0}`);

      // Status History
      if (details?.status_history && details.status_history.length > 0) {
        addSection('Status History');
        details.status_history.forEach((entry: any, index: number) => {
          addText(`${index + 1}. ${formatDateTime(entry.changed_at)}`, 10, true);
          addText(`   ${entry.old_status || 'NONE'} → ${entry.new_status}`);
          if (entry.changed_by) addText(`   Changed by: ${entry.changed_by}`);
          if (entry.reason) addText(`   Reason: ${entry.reason}`);
          if (entry.comment) addText(`   Comment: ${entry.comment}`);
          yPos += 2;
        });
      }

      // Comments
      if (details?.comments && details.comments.length > 0) {
        addSection(`Comments (${details.comments.length})`);
        details.comments.forEach((comment: any) => {
          addText(`#${comment.number} - ${comment.author} [${comment.type}]`, 9, true);
          addText(`${formatDateTime(comment.timestamp)}`, 8);
          addText(comment.text, 9);
          yPos += 3;
        });
      }

      // Footer
      const pageCount = doc.internal.pages.length - 1;
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(
          `Page ${i} of ${pageCount} - Generated ${new Date().toLocaleString()}`,
          margin,
          doc.internal.pageSize.getHeight() - 10
        );
      }

      // Save PDF
      doc.save(`call-report-${call.incident_number}.pdf`);
      console.log(`[CLOSED CALLS] PDF generated client-side for ${call.incident_number}`);
    } catch (err) {
      console.error('[CLOSED CALLS] Error generating PDF:', err);
      alert('Failed to generate PDF report');
    }
  };

  return (
    <div className="closed-calls-overlay">
      <div className="closed-calls-container">
        {/* Header */}
        <div className="closed-calls-header">
          <h2><Icon icon="material-symbols:folder-open" width="24" height="24" style={{ verticalAlign: 'middle', marginRight: '8px' }} /> Closed Calls History</h2>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button 
              className="refresh-button" 
              onClick={handleRefresh}
              disabled={refreshing}
              title="Refresh data"
            >
              <Icon icon="material-symbols:refresh" width="20" height="20" className={refreshing ? 'spinning' : ''} />
            </button>
          <button className="close-button" onClick={onClose}><Icon icon="material-symbols:close" width="20" height="20" /></button>
          </div>
        </div>

        {/* Metrics Summary */}
        {calculatedMetrics && (
          <div className="metrics-summary">
            <h3>Metrics Summary {filteredCalls.length < allCalls.length && `(Filtered: ${filteredCalls.length} of ${allCalls.length})`}</h3>
            <div className="metrics-grid">
              <div className="metric-card">
                <div className="metric-label">Total Calls</div>
                <div className="metric-value">{calculatedMetrics.total_calls || 0}</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Avg Response Time</div>
                <div className="metric-value">
                  {formatDuration(Math.floor(calculatedMetrics.avg_response_time))}
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Avg On-Scene Time</div>
                <div className="metric-value">
                  {formatDuration(Math.floor(calculatedMetrics.avg_on_scene_time))}
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Avg Total Duration</div>
                <div className="metric-value">
                  {formatDuration(Math.floor(calculatedMetrics.avg_total_duration))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="filters-section">
          <div style={{ position: 'relative', width: '100%' }}>
            <Icon icon="material-symbols:search" width="20" height="20" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#b4b9c0', zIndex: 1 }} />
            <input
              type="text"
              placeholder="Search location, notes, or incident #..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="search-input"
              style={{ paddingLeft: '36px', backgroundImage: 'none' }}
            />
          </div>
          
          <div className="filter-row">
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setCurrentPage(1);
              }}
              className="date-input"
              placeholder="Start Date"
            />
            
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setCurrentPage(1);
              }}
              className="date-input"
              placeholder="End Date"
            />
            
            <button 
              className="clear-filters-btn"
              onClick={() => {
                setSearchTerm('');
                setStartDate('');
                setEndDate('');
                setSelectedCallCode('');
                setCurrentPage(1);
              }}
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Calls List */}
        <div className="closed-calls-content">
          {loading && <div className="loading">Loading closed calls...</div>}
          
          {error && <div className="error-message">{error}</div>}
          
          {!loading && !error && filteredCalls.length === 0 && (
            <div className="no-calls">
              {allCalls.length > 0 ? 'No calls match your filters' : 'No closed calls found'}
            </div>
          )}

          {!loading && !error && filteredCalls.length > 0 && (
            <>
              <div className="calls-table-wrapper">
                <table className="closed-calls-table">
                  <thead>
                    <tr>
                      <th>Incident #</th>
                      <th>Code</th>
                      <th>Location</th>
                      <th>Priority</th>
                      <th>Closed At</th>
                      <th>Closed By</th>
                      <th>Duration</th>
                      <th>Response Time</th>
                      <th>Units</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedCalls.map((call) => (
                      <tr key={call.id} className={`priority-${call.priority}`}>
                        <td className="incident-cell">
                          {call.incident_number}
                          {call.reopen_count > 0 && (
                            <span className="reopen-badge" title={`Reopened ${call.reopen_count} time(s)`}>
                              <Icon icon="material-symbols:refresh" width="14" height="14" style={{ verticalAlign: 'middle', marginRight: '2px' }} />{call.reopen_count}
                            </span>
                          )}
                        </td>
                        <td className="code-cell">{call.call_code}</td>
                        <td className="location-cell">{call.location}</td>
                        <td className="priority-cell">
                          <span className={`priority-badge p${call.priority}`}>
                            P{call.priority}
                          </span>
                        </td>
                        <td className="time-cell">{formatDateTime(call.closed_at)}</td>
                        <td className="user-cell">{call.closed_by}</td>
                        <td className="metric-cell">{formatDuration(call.total_duration)}</td>
                        <td className="metric-cell">{formatDuration(call.response_time)}</td>
                        <td className="units-cell">
                          {call.units_responded_count || 0} / {call.units_assigned_count || 0}
                        </td>
                        <td className="actions-cell">
                          <button
                            className="action-btn view-btn"
                            onClick={() => fetchCallDetails(call)}
                            title="View Details"
                          >
                            <Icon icon="material-symbols:visibility" width="18" height="18" />
                          </button>
                          {isDispatcher && (
                            <>
                              <button
                                className="action-btn reopen-btn"
                                onClick={() => {
                                  setSelectedCall(call);
                                  setShowReopenModal(true);
                                }}
                                title="Reopen Call"
                              >
                                <Icon icon="material-symbols:replay" width="18" height="18" />
                              </button>
                            </>
                          )}
                          <button
                            className="action-btn pdf-btn"
                            onClick={() => exportToPDF(call)}
                            title="Export to PDF"
                          >
                            <Icon icon="material-symbols:picture-as-pdf" width="18" height="18" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="pagination">
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                  className="page-btn"
                >
                  ← Previous
                </button>
                
                <span className="page-info">
                  Page {currentPage} of {totalPages} ({filteredCalls.length} {filteredCalls.length !== allCalls.length ? `filtered / ${allCalls.length} total` : 'total'})
                </span>
                
                <button
                  disabled={!hasMore}
                  onClick={() => setCurrentPage(p => p + 1)}
                  className="page-btn"
                >
                  Next →
                </button>
              </div>
            </>
          )}
        </div>

        {/* Call Details Modal */}
        {showDetailsModal && callDetails && selectedCall && (
          <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
            <div className="modal-content details-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Call Details: {selectedCall.incident_number}</h3>
                <button onClick={() => setShowDetailsModal(false)}><Icon icon="material-symbols:close" width="20" height="20" /></button>
              </div>

              <div className="modal-body">
                {/* Call Info */}
                <div className="details-section">
                  <h4><Icon icon="material-symbols:phone-in-talk" width="20" height="20" style={{ verticalAlign: 'middle', marginRight: '6px' }} /> Call Information</h4>
                  <div className="details-grid">
                    <div><strong>Code:</strong> {selectedCall.call_code}</div>
                    <div><strong>Priority:</strong> P{selectedCall.priority}</div>
                    <div><strong>Location:</strong> {selectedCall.location}</div>
                    <div><strong>Created:</strong> {formatDateTime(selectedCall.created_at)}</div>
                    <div><strong>Closed:</strong> {formatDateTime(selectedCall.closed_at)}</div>
                    <div><strong>Closed By:</strong> {selectedCall.closed_by}</div>
                  </div>
                  {selectedCall.notes && (
                    <div className="notes-box">
                      <strong>Notes:</strong> {selectedCall.notes}
                    </div>
                  )}
                </div>

                {/* Metrics Timeline */}
                <div className="details-section">
                  <h4><Icon icon="material-symbols:schedule" width="20" height="20" style={{ verticalAlign: 'middle', marginRight: '6px' }} /> Call Timeline & Metrics</h4>
                  
                  {!selectedCall.first_unit_assigned_at && !selectedCall.first_unit_enroute_at && !selectedCall.first_unit_onscene_at ? (
                    /* Legacy call without detailed tracking */
                    <div className="legacy-metrics-notice">
                      <Icon icon="material-symbols:info" width="20" height="20" style={{ marginRight: '8px' }} />
                      <div>
                        <strong>Limited Metrics Available</strong>
                        <p>This call was closed before detailed unit tracking was implemented.</p>
                      </div>
                    </div>
                  ) : null}

                  {/* Timestamps & Durations Table */}
                  <div className="metrics-table-container">
                    <table className="metrics-table">
                      <thead>
                        <tr>
                          <th>Event</th>
                          <th>Timestamp</th>
                          <th>Duration</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="event-name">Call Created (Pending)</td>
                          <td className="timestamp-cell">{formatDateTime(selectedCall.created_at)}</td>
                          <td className="duration-cell">-</td>
                        </tr>
                        
                        {selectedCall.first_unit_assigned_at && (
                          <tr>
                            <td className="event-name">First Unit Assigned (Active)</td>
                            <td className="timestamp-cell">{formatDateTime(selectedCall.first_unit_assigned_at)}</td>
                            <td className="duration-cell metric-highlight">
                              {formatDuration(selectedCall.dispatch_time)}
                              <span className="metric-label-inline">dispatch time</span>
                            </td>
                          </tr>
                        )}

                        {selectedCall.first_unit_enroute_at && (
                          <tr>
                            <td className="event-name">First Unit Enroute</td>
                            <td className="timestamp-cell">{formatDateTime(selectedCall.first_unit_enroute_at)}</td>
                            <td className="duration-cell metric-highlight">
                              {formatDuration(selectedCall.response_time)}
                              <span className="metric-label-inline">response time</span>
                            </td>
                          </tr>
                        )}

                        {selectedCall.first_unit_onscene_at && (
                          <tr>
                            <td className="event-name">First Unit On Scene</td>
                            <td className="timestamp-cell">{formatDateTime(selectedCall.first_unit_onscene_at)}</td>
                            <td className="duration-cell metric-highlight">
                              {formatDuration(selectedCall.enroute_time)}
                              <span className="metric-label-inline">enroute time</span>
                            </td>
                          </tr>
                        )}

                        {selectedCall.first_unit_transport_at && (
                          <tr>
                            <td className="event-name">First Unit Transport (TRX)</td>
                            <td className="timestamp-cell">{formatDateTime(selectedCall.first_unit_transport_at)}</td>
                            <td className="duration-cell metric-highlight">
                              {formatDuration(selectedCall.transport_time)}
                              <span className="metric-label-inline">on-scene time</span>
                            </td>
                          </tr>
                        )}

                        <tr className="closed-row">
                          <td className="event-name">Call Closed</td>
                          <td className="timestamp-cell">{formatDateTime(selectedCall.closed_at)}</td>
                          <td className="duration-cell metric-highlight">
                            {!selectedCall.first_unit_transport_at && selectedCall.first_unit_onscene_at && (
                              <>
                                {formatDuration(selectedCall.on_scene_time)}
                                <span className="metric-label-inline">on-scene time</span>
                              </>
                            )}
                          </td>
                        </tr>

                        <tr className="total-row">
                          <td className="event-name"><strong>Total Call Duration</strong></td>
                          <td className="timestamp-cell">-</td>
                          <td className="duration-cell total-duration">
                            <strong>{formatDuration(selectedCall.total_duration)}</strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Per-Unit Breakdown */}
                  {(() => {
                    let unitData: { [key: string]: any } = {};
                    
                    // Use unit_breakdown from metrics if available
                    if (selectedCall.unit_breakdown && typeof selectedCall.unit_breakdown === 'object') {
                      unitData = selectedCall.unit_breakdown;
                    } else if (selectedCall.units && Object.keys(selectedCall.units).length > 0) {
                      // Fallback to existing units data
                      unitData = selectedCall.units;
                    } else {
                      // Final fallback: no unit data available
                      unitData = {};
                      console.log('No unit data available for closed call:', selectedCall.id);
                    }
                    
                    const unitEntries = Object.entries(unitData);
                    
                    return unitEntries.length > 0 ? (
                      <div className="unit-breakdown-section">
                        <h5><Icon icon="material-symbols:local-police" width="18" height="18" style={{ verticalAlign: 'middle', marginRight: '6px' }} /> Unit Breakdown</h5>
                        <table className="unit-breakdown-table">
                          <thead>
                            <tr>
                              <th>Unit</th>
                              <th>Latest Status</th>
                              <th>Attached At</th>
                              <th>Status Changes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {unitEntries.map(([unitId, unitInfo]: [string, any]) => (
                              <tr key={unitId}>
                                <td className="unit-id">{unitId}</td>
                                <td className="unit-status">
                                  <span className={`status-badge ${unitInfo.latest_status?.toLowerCase().replace('/', '-')}`}>
                                    {unitInfo.latest_status || 'Unknown'}
                                  </span>
                                </td>
                                <td className="timestamp-cell">
                                  {unitInfo.attached_at ? formatDateTime(unitInfo.attached_at) : 'N/A'}
                                </td>
                                <td className="status-changes-cell">
                                  {unitInfo.status_changes && unitInfo.status_changes.length > 0 ? (
                                    <div className="status-changes">
                                      {unitInfo.status_changes.map((change: any, index: number) => (
                                        <div key={index} className="status-change">
                                          <span className="status-badge small">{change.status}</span>
                                          <span className="change-time">{formatDateTime(change.timestamp)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    'No status changes'
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="no-units-message">
                        <Icon icon="material-symbols:info" width="16" height="16" style={{ marginRight: '8px' }} />
                        <span>No unit tracking data available for this call.</span>
                      </div>
                    );
                  })()}

                  <div className="timeline-container" style={{display: 'none'}}>
                    {/* Call Created */}
                    <div className="timeline-item">
                      <div className="timeline-marker pending"></div>
                      <div className="timeline-content">
                        <div className="timeline-title">Call Created (Pending)</div>
                        <div className="timeline-time">{formatDateTime(selectedCall.created_at)}</div>
                      </div>
                    </div>

                    {/* First Unit Assigned */}
                    {selectedCall.first_unit_assigned_at && (
                      <div className="timeline-item">
                        <div className="timeline-marker active"></div>
                        <div className="timeline-content">
                          <div className="timeline-title">First Unit Assigned (Active)</div>
                          <div className="timeline-time">{formatDateTime(selectedCall.first_unit_assigned_at)}</div>
                          <div className="timeline-duration">Dispatch Time: {formatDuration(selectedCall.dispatch_time)}</div>
                        </div>
                      </div>
                    )}

                    {/* First Unit Enroute */}
                    {selectedCall.first_unit_enroute_at && (
                      <div className="timeline-item">
                        <div className="timeline-marker enroute"></div>
                        <div className="timeline-content">
                          <div className="timeline-title">First Unit Enroute</div>
                          <div className="timeline-time">{formatDateTime(selectedCall.first_unit_enroute_at)}</div>
                          <div className="timeline-duration">Response Time: {formatDuration(selectedCall.response_time)} (time to acknowledge)</div>
                        </div>
                      </div>
                    )}

                    {/* First Unit On Scene */}
                    {selectedCall.first_unit_onscene_at && (
                      <div className="timeline-item">
                        <div className="timeline-marker onscene"></div>
                        <div className="timeline-content">
                          <div className="timeline-title">First Unit On Scene</div>
                          <div className="timeline-time">{formatDateTime(selectedCall.first_unit_onscene_at)}</div>
                          <div className="timeline-duration">Enroute Time: {formatDuration(selectedCall.enroute_time)}</div>
                        </div>
                      </div>
                    )}

                    {/* Call Closed */}
                    <div className="timeline-item">
                      <div className="timeline-marker closed"></div>
                      <div className="timeline-content">
                        <div className="timeline-title">Call Closed</div>
                        <div className="timeline-time">{formatDateTime(selectedCall.closed_at)}</div>
                        {selectedCall.first_unit_onscene_at && (
                          <div className="timeline-duration">On-Scene Time: {formatDuration(selectedCall.on_scene_time)}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="metrics-summary-box">
                    <div className="metric-item">
                      <span className="metric-label">Total Duration:</span>
                      <span className="metric-value">{formatDuration(selectedCall.total_duration)}</span>
                    </div>
                    <div className="metric-item">
                      <span className="metric-label">Units Assigned:</span>
                      <span className="metric-value">{selectedCall.units_assigned_count || 0}</span>
                    </div>
                    <div className="metric-item">
                      <span className="metric-label">Units Responded:</span>
                      <span className="metric-value">{selectedCall.units_responded_count || 0}</span>
                    </div>
                  </div>
                </div>

                {/* Status History */}
                {callDetails.status_history && callDetails.status_history.length > 0 && (
                  <div className="details-section">
                    <h4><Icon icon="material-symbols:history" width="20" height="20" style={{ verticalAlign: 'middle', marginRight: '6px' }} /> Status History</h4>
                    <div className="history-list">
                      {callDetails.status_history.map((entry: any) => (
                        <div key={entry.id} className="history-item">
                          <div className="history-time">{formatDateTime(entry.changed_at)}</div>
                          <div className="history-change">
                            {entry.old_status && <span className="status-badge old">{entry.old_status}</span>}
                            →
                            <span className="status-badge new">{entry.new_status}</span>
                          </div>
                          <div className="history-user">by {entry.changed_by}</div>
                          {entry.reason && <div className="history-reason">{entry.reason}</div>}
                          {entry.comment && <div className="history-comment">{entry.comment}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Comments */}
                {callDetails.comments && callDetails.comments.length > 0 && (
                  <div className="details-section">
                    <h4><Icon icon="material-symbols:comment" width="20" height="20" style={{ verticalAlign: 'middle', marginRight: '6px' }} /> Comments ({callDetails.comments.length})</h4>
                    <div className="comments-list">
                      {callDetails.comments.map((comment: any) => (
                        <div key={comment.id} className={`comment-item ${comment.type.toLowerCase()}`}>
                          <div className="comment-header">
                            <span className="comment-number">#{comment.number}</span>
                            <span className="comment-author">{comment.author}</span>
                            <span className="comment-time">{formatDateTime(comment.timestamp)}</span>
                            <span className={`comment-type ${comment.type.toLowerCase()}`}>{comment.type}</span>
                          </div>
                          <div className="comment-text">{comment.text}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                {isDispatcher && (
                  <div className="modal-actions">
                    <button
                      className="reopen-call-btn"
                      onClick={() => {
                        setShowDetailsModal(false);
                        setShowReopenModal(true);
                      }}
                    >
                      <Icon icon="material-symbols:replay" width="18" height="18" style={{ verticalAlign: 'middle', marginRight: '6px' }} /> Reopen Call
                    </button>
                    <button
                      className="export-pdf-btn"
                      onClick={() => exportToPDF(selectedCall)}
                    >
                      <Icon icon="material-symbols:picture-as-pdf" width="18" height="18" style={{ verticalAlign: 'middle', marginRight: '6px' }} /> Export PDF Report
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Reopen Modal */}
        {showReopenModal && selectedCall && (
          <div className="modal-overlay" onClick={() => setShowReopenModal(false)}>
            <div className="modal-content reopen-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3><Icon icon="material-symbols:replay" width="20" height="20" style={{ verticalAlign: 'middle', marginRight: '6px' }} /> Reopen Call {selectedCall.incident_number}</h3>
                <button onClick={() => setShowReopenModal(false)}><Icon icon="material-symbols:close" width="20" height="20" /></button>
              </div>

              <div className="modal-body">
                <div className="form-group">
                  <label>Reason for Reopening:</label>
                  <select
                    value={reopenReason}
                    onChange={(e) => setReopenReason(e.target.value)}
                    className="reason-select"
                  >
                    <option value="Additional response needed">Additional response needed</option>
                    <option value="Incident not resolved">Incident not resolved</option>
                    <option value="New information received">New information received</option>
                    <option value="Units need to return">Units need to return</option>
                    <option value="Premature closure">Premature closure</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Comment (optional):</label>
                  <textarea
                    value={reopenComment}
                    onChange={(e) => setReopenComment(e.target.value)}
                    placeholder="Add additional details..."
                    rows={4}
                    className="comment-textarea"
                  />
                </div>

                <div className="warning-box">
                  <Icon icon="material-symbols:warning" width="20" height="20" style={{ verticalAlign: 'middle', marginRight: '6px' }} /> Reopening this call will set its status to PENDING and it will appear in the active calls list.
                </div>
              </div>

              <div className="modal-footer">
                <button onClick={() => setShowReopenModal(false)} className="cancel-btn">
                  Cancel
                </button>
                <button onClick={handleReopenCall} className="confirm-btn">
                  Reopen Call
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClosedCallsView;

